import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Pause, Play, TrendingUp, CheckCircle2, AlertCircle,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { adaptiveMacrosApi } from '../api/adaptiveMacros';
import { measurementsApi } from '../api/measurements';
import InfoButton from '../components/ui/info-button';

const daysBetween = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24)));
};

const ReverseDietPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const clientId = params.get('client_id') || null;

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [pausing, setPausing] = useState(false);

  const loadAll = async () => {
    const [cfgRes, adjRes, mRes] = await Promise.all([
      adaptiveMacrosApi.getConfig(clientId),
      adaptiveMacrosApi.listAdjustments(clientId, 200),
      measurementsApi.getAll(clientId ? { user_id: clientId } : {}),
    ]);
    setConfig(cfgRes?.data?.config || null);
    setAdjustments(adjRes?.data?.adjustments || []);
    setMeasurements((mRes?.data || []).filter((m) => m.weight_kg != null));
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try { await loadAll(); } catch { /* */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const reverseCfg = useMemo(() => config?.reverse_config || {}, [config]);
  const startedAt = reverseCfg.started_at;
  const completed = !!reverseCfg.completed_at;
  const paused = !!reverseCfg.paused_at && !completed;
  const targetMaint = reverseCfg.target_maintenance_kcal;
  const weeklyIncrease = reverseCfg.weekly_kcal_increase;
  const weekNumber = reverseCfg.week_number || 0;

  // Build kcal trajectory from adjustments
  const kcalSeries = useMemo(() => {
    if (!config) return [];
    const byDate = new Map();
    for (const a of adjustments) {
      const d = (a.adjusted_at || '').slice(0, 10);
      if (!d) continue;
      byDate.set(d, a.new_targets?.kcal ?? null);
    }
    // Ensure starting point
    if (startedAt) {
      const d0 = startedAt.slice(0, 10);
      if (!byDate.has(d0) && adjustments.length) {
        const first = adjustments[adjustments.length - 1];
        byDate.set(d0, first.previous_targets?.kcal ?? null);
      }
    }
    const dates = Array.from(byDate.keys()).sort();
    return dates.map((d) => ({ date: d, kcal: byDate.get(d) }));
  }, [adjustments, config, startedAt]);

  const chartData = useMemo(() => {
    const map = new Map();
    for (const p of kcalSeries) {
      map.set(p.date, { date: p.date, kcal: p.kcal, weight: null });
    }
    for (const m of measurements) {
      const d = (m.date || '').slice(0, 10);
      if (!d) continue;
      const existing = map.get(d) || { date: d, kcal: null, weight: null };
      existing.weight = Number(m.weight_kg);
      map.set(d, existing);
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [kcalSeries, measurements]);

  const progressPct = useMemo(() => {
    if (!targetMaint || !config?.current_targets?.kcal || !reverseCfg.start_kcal) return 0;
    const span = targetMaint - reverseCfg.start_kcal;
    if (span <= 0) return 100;
    const done = config.current_targets.kcal - reverseCfg.start_kcal;
    return Math.max(0, Math.min(100, Math.round((done / span) * 100)));
  }, [config, targetMaint, reverseCfg]);

  const handlePauseToggle = async () => {
    // Implemented as disable/enable for now — deeper pause lives on the config
    setPausing(true);
    try {
      if (paused) {
        // resume = no-op on server state (requires backend pause/resume endpoint to fully support)
        toast.info('To resume, re-enable via settings');
      } else {
        await adaptiveMacrosApi.disable(clientId);
        toast.success('Reverse diet paused');
        await loadAll();
      }
    } finally {
      setPausing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (!config || config.goal !== 'reverse') {
    return (
      <div className="max-w-xl mx-auto p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Reverse Diet</h1>
          <InfoButton contentKey="reverseDiet" />
        </div>
        <Card>
          <CardContent className="py-10 text-center space-y-3">
            <TrendingUp className="h-10 w-10 text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground">
              Reverse Diet isn't active. Start one from Adaptive Macros settings.
            </p>
            <Button onClick={() => navigate('/adaptive-macros/settings' + (clientId ? `?client_id=${clientId}` : ''))}>
              Go to settings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6 pb-20">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">Reverse Diet</h1>
        <InfoButton contentKey="reverseDiet" />
      </div>

      <Card className="bg-gradient-to-br from-zinc-900 via-zinc-950 to-black border-white/10 text-white overflow-hidden relative">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#FF5C00]/20 blur-3xl" />
        <div className="relative p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {completed ? (
                <CheckCircle2 className="h-5 w-5 text-green-400" />
              ) : paused ? (
                <AlertCircle className="h-5 w-5 text-yellow-400" />
              ) : (
                <TrendingUp className="h-5 w-5 text-[#FF5C00]" />
              )}
              <div className="font-semibold">
                Week {weekNumber} {targetMaint ? `→ ${targetMaint} kcal` : ''}
              </div>
            </div>
            <Badge variant="outline" className="border-white/20 text-white">
              {completed ? 'Complete' : paused ? 'Paused' : 'In progress'}
            </Badge>
          </div>
          <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[#FF5C00] transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-white/60">
            <span>{reverseCfg.start_kcal ?? config.current_targets?.kcal ?? '—'} kcal</span>
            <span>{progressPct}%</span>
            <span>{targetMaint ?? '—'} kcal</span>
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-white/60 pt-1">
            <span>+{weeklyIncrease ?? '—'} kcal/wk</span>
            <span>·</span>
            <span>{daysBetween(startedAt)} days since start</span>
            {reverseCfg.max_weight_gain_kg != null && (
              <>
                <span>·</span>
                <span>Pause &gt;{reverseCfg.max_weight_gain_kg} kg/wk</span>
              </>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Kcal &amp; weight over time</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Data will appear after your first check-in.
            </div>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis yAxisId="kcal" orientation="left" fontSize={11} />
                  <YAxis yAxisId="weight" orientation="right" fontSize={11} />
                  <Tooltip />
                  <Legend />
                  {targetMaint && (
                    <ReferenceLine
                      yAxisId="kcal"
                      y={targetMaint}
                      stroke="#FF5C00"
                      strokeDasharray="4 4"
                      label={{ value: 'Target', fontSize: 10, fill: '#FF5C00' }}
                    />
                  )}
                  <Line
                    yAxisId="kcal"
                    type="stepAfter"
                    dataKey="kcal"
                    stroke="#FF5C00"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    name="kcal"
                  />
                  <Line
                    yAxisId="weight"
                    type="monotone"
                    dataKey="weight"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                    name="weight (kg)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Adjustments log</CardTitle>
        </CardHeader>
        <CardContent>
          {adjustments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No adjustments yet.</p>
          ) : (
            <ul className="divide-y divide-border/60">
              {adjustments.map((a) => {
                const delta = (a.new_targets?.kcal || 0) - (a.previous_targets?.kcal || 0);
                return (
                  <li key={a.id} className="py-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">
                        {delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : 'Held'} kcal
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{a.reason}</div>
                    </div>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {new Date(a.adjusted_at).toLocaleDateString()}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {!completed && (
        <>
          <Separator />
          <Button
            variant="outline"
            className="w-full"
            onClick={handlePauseToggle}
            disabled={pausing}
          >
            {paused ? (
              <><Play className="h-4 w-4 mr-2" /> Resume</>
            ) : (
              <><Pause className="h-4 w-4 mr-2" /> Pause reverse diet</>
            )}
          </Button>
        </>
      )}
    </div>
  );
};

export default ReverseDietPage;
